import type { FolderNode } from '@/features/tree/buildTree';

/**
 * Mise à plat de l'arborescence pour un sélecteur de destination.
 *
 * EXTRAITE DU COMPOSANT parce que c'est la partie qui peut être FAUSSE sans que
 * rien ne se voie : un chemin tronqué, un doublon non détecté, une recherche qui
 * ignore le parent. Ici elle est éprouvée directement.
 */
export interface FolderOption {
  id: string;
  /** Nom du dossier, seul. */
  name: string;
  /** Profondeur, pour l'indentation de la liste. */
  depth: number;
  /** Chemin complet lisible, du plus haut au dossier lui-même : « FLUIDES / CCRS ». */
  path: string;
  /**
   * Chemin des PARENTS seulement, ou chaîne vide à la racine.
   *
   * Affiché en gris derrière le nom UNIQUEMENT quand le nom est ambigu (cf.
   * `ambiguous`) : le montrer partout allongerait chaque ligne pour ne lever un
   * doute qu'une fois sur dix.
   */
  parentPath: string;
  /**
   * Vrai quand un AUTRE dossier de l'espace porte le même nom. C'est le cas qui
   * rend une liste indentée trompeuse : deux « CCRS » à des niveaux différents
   * sont indiscernables dès que la liste se referme.
   */
  ambiguous: boolean;
  /** Tout ce sur quoi la recherche porte, déjà normalisé. */
  haystack: string;
}

/** Minuscules et sans accents : « Générales » se trouve en tapant « generales ». */
export function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    // `\p{Diacritic}` et non une plage de codes : une plage s'écrit soit avec les
    // caractères combinants eux-mêmes — invisibles dans un éditeur, donc
    // impossibles à relire — soit en échappements qu'il faut vérifier un par un.
    // La classe nommée dit ce qu'elle enlève.
    .replace(/\p{Diacritic}/gu, '');
}

export function flattenFolderOptions(nodes: FolderNode[]): FolderOption[] {
  const out: FolderOption[] = [];

  const walk = (list: FolderNode[], depth: number, ancestors: string[]) => {
    for (const n of list) {
      const name = n.folder.name;
      const parentPath = ancestors.join(' / ');
      const path = [...ancestors, name].join(' / ');
      out.push({
        id: n.folder.id,
        name,
        depth,
        path,
        parentPath,
        // Renseigné en second passage : on ne connaît les homonymes qu'à la fin.
        ambiguous: false,
        // LA RECHERCHE PORTE SUR LE CHEMIN ENTIER, pas sur le seul nom : taper
        // « fluides ccrs » doit trouver le CCRS qui est SOUS Fluides, et c'est la
        // seule façon de viser un homonyme sans parcourir la liste à l'œil.
        haystack: normalize(path),
      });
      walk(n.children, depth + 1, [...ancestors, name]);
    }
  };
  walk(nodes, 0, []);

  const counts = new Map<string, number>();
  for (const o of out) counts.set(normalize(o.name), (counts.get(normalize(o.name)) ?? 0) + 1);
  for (const o of out) o.ambiguous = (counts.get(normalize(o.name)) ?? 0) > 1;

  return out;
}

/**
 * Filtre par mots : chaque mot saisi doit se trouver dans le chemin, dans
 * n'importe quel ordre. « ccrs fluides » et « fluides ccrs » donnent donc le même
 * résultat — personne ne se souvient de l'ordre exact d'une arborescence.
 */
export function filterFolderOptions(options: FolderOption[], query: string): FolderOption[] {
  const words = normalize(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return options;
  return options.filter((o) => words.every((w) => o.haystack.includes(w)));
}
